import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getUser, signIn } from 'balkerne-core/auth'
import authActions from '../store/auth'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import siteMap from '../siteMap'
import BackgroundImage from '../images/login-background.jpg'
import { Box, Button, Paper, Stack, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import webLogo from '../images/logo_web.png'
import { exchangeCodeForTokens, getTenantInfo, storeTokens } from 'balkerne-core/federatedAuth'
import { useLocation } from 'react-router-dom'

enum SSOEnum {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

const Login = () => {
  const params = new URLSearchParams(useLocation().search)
  const code = params.get('code')
  const dispatch = useDispatch()
  const history = useHistory()
  const defaultEmail = useMemo(() => {
    const storedEmail = localStorage.getItem('user')
    return code && storedEmail ? storedEmail : ''
  }, [code])
  const [email, setEmail] = useState(defaultEmail)
  const [isFederated, setIsFederated] = useState<SSOEnum>(SSOEnum.UNKNOWN)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>('')
  const [success, setSuccess] = useState(true)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const shouldSelectEmailRef = useRef(false)

  useEffect(() => {
    setEmail(defaultEmail)
  }, [defaultEmail])

  useEffect(() => {
    if (isFederated === SSOEnum.UNKNOWN && shouldSelectEmailRef.current && emailInputRef.current) {
      emailInputRef.current.focus()
      emailInputRef.current.select()
      shouldSelectEmailRef.current = false
    }
  }, [isFederated])

  useEffect(() => {
    if (!code) return
    const handleFederatedSignIn = async () => {
      setIsLoading(true)
      const tokens = await exchangeCodeForTokens(code)
      if (!tokens) {
        setIsLoading(false)
        setError('Authentication failed')
        setSuccess(false)
        return
      }
      storeTokens(tokens)

      const user = await getUser()
      if (!user) {
        setIsLoading(false)
        setError("Couldn't fetch user information")
        setSuccess(false)
        return
      }
      dispatch(authActions.login({ user }))
      history.push(siteMap.Portfolio.path)
      setIsLoading(false)
    }
    handleFederatedSignIn()
  }, [code, dispatch, history])

  const forgotPassword = () => {
    history.push({
      pathname: '/ResetPassword',
    })
  }

  const handleGetTenant = useCallback(async (submittedEmail: string) => {
    setIsLoading(true)
    setError(null)
    const data = await getTenantInfo(submittedEmail)

    if (data) {
      setIsFederated(data.Federation ? SSOEnum.ENABLED : SSOEnum.DISABLED)
      localStorage.setItem('federation', String(data.Federation))
      localStorage.setItem('user', submittedEmail)
      setSuccess(true)

      if (data.Federation) {
        window.location.href = data.LoginUrl
        return
      }
    } else {
      setIsFederated(SSOEnum.UNKNOWN)
      setSuccess(false)
      setError('User account not found. Please contact your administrator.')
    }
    setIsLoading(false)
  }, [])

  const handleSignIn = async (submittedEmail: string, submittedPassword: string) => {
    setIsLoading(true)

    try {
      if (submittedEmail?.length > 0 && !submittedEmail.toLowerCase().endsWith('arcusfm.com')) {
        try {
          const { token, user } = await signIn(submittedEmail, submittedPassword)
          dispatch(authActions.login({ token, user }))
          history.push(siteMap.Portfolio.path)
        } catch (err) {
          console.error('First sign in attempt failed:', err)
          try {
            const { token, user } = await signIn(submittedEmail, submittedPassword)
            dispatch(authActions.login({ token, user }))
            history.push(siteMap.Portfolio.path)
          } catch (err2: any) {
            console.error('Second sign in attempt failed:', err2)
            const errorCode = err2?.code ?? undefined
            let errorMessage = err2?.message ?? 'Error occurred, please try again later or contact support'
            if (errorCode === 'UserNotFoundException') {
              errorMessage = 'User account not found. Please contact your administrator.'
            }
            setError(errorMessage)
            setSuccess(false)
          }
        }
      } else {
        setError('Please use your organisation email address.')
        setSuccess(false)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const resetToIdentifierStep = useCallback(() => {
    localStorage.removeItem('user')
    localStorage.removeItem('federation')
    shouldSelectEmailRef.current = true
    setIsFederated(SSOEnum.UNKNOWN)
    setError(null)
    setSuccess(true)
  }, [])

  const handleEmailChange = useCallback(
    e => {
      setEmail(e.target.value)
      if (error || !success) {
        setError(null)
        setSuccess(true)
      }
    },
    [error, success],
  )

  const showNotice = () => {
    if (!success) {
      return (
        <div className="alert alert-danger" role="alert">
          {error ?? 'Error occurred, please try again later or contact support'}
        </div>
      )
    }
  }

  const isPasswordStep = isFederated === SSOEnum.DISABLED

  const handleSubmit = async e => {
    e.preventDefault()
    if (isLoading) return

    const formData = new FormData(e.currentTarget)
    const submittedEmail = String(formData.get('username') ?? '').trim()
    const submittedPassword = String(formData.get('password') ?? '')

    if (!isPasswordStep) {
      setEmail(submittedEmail)
    }

    if (isPasswordStep) {
      await handleSignIn(submittedEmail, submittedPassword)
    } else {
      await handleGetTenant(submittedEmail)
    }
  }

  return (
    <Box
      sx={{
        overflow: 'hidden',
        position: 'relative',
      }}>
      <Box
        component={'img'}
        src={BackgroundImage}
        sx={{
          '@keyframes slow-pan': {
            '0%': {
              transform: 'translate(-5%, -5%) scale(1) rotate(-5deg)',
            },
            '100%': {
              transform: 'translate(-15%, -15%) scale(1.15) rotate(15deg)',
            },
          },
          animation: 'slow-pan 30s cubic-bezier(.21,0,.7,1) infinite',
          animationDelay: '-5s',
          animationDirection: 'alternate',
          position: 'absolute',
          top: 0,
          left: 0,
          minWidth: '120vw',
          minHeight: '120vh',
          zIndex: 0,
        }}
      />

      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{
          height: '100vh',
          width: '100vw',
          backgroundColor: '#000000' + 'AD',
          p: 2,
          zIndex: 1,
          position: 'relative',
        }}>
        <Paper sx={{ width: '100%', maxWidth: 400, p: 4 }}>
          <Stack alignItems="stretch" spacing={2}>
            <Stack alignItems="center" mb={2}>
              <img src={webLogo} width={280} height="auto" />
            </Stack>
            {isPasswordStep ? (
              <Stack key="password-step" component="form" spacing={2} autoComplete="on" onSubmit={handleSubmit}>
                <TextField
                  label="Email"
                  type="email"
                  id="username"
                  name="username"
                  autoComplete="username"
                  required
                  fullWidth
                  defaultValue={email}
                  disabled={isLoading}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  autoFocus
                  label="Password"
                  type="password"
                  id="current-password"
                  name="password"
                  autoComplete="current-password"
                  required
                  fullWidth
                  disabled={isLoading}
                />
                <Box />
                <LoadingButton loading={isLoading} variant="contained" size="large" fullWidth type="submit">
                  Login
                </LoadingButton>
                <Button variant="text" fullWidth type="button" onClick={resetToIdentifierStep}>
                  Use a different email
                </Button>
                <Button variant="text" fullWidth type="button" onClick={forgotPassword}>
                  Forgot Password
                </Button>
              </Stack>
            ) : (
              <Stack key="identifier-step" component="form" spacing={2} autoComplete="on" onSubmit={handleSubmit}>
                <TextField
                  autoFocus
                  label="Email"
                  type="email"
                  id="username"
                  name="username"
                  autoComplete="username"
                  required
                  fullWidth
                  value={email}
                  disabled={isLoading}
                  inputRef={emailInputRef}
                  onChange={handleEmailChange}
                />
                <Box />
                <LoadingButton loading={isLoading} variant="contained" size="large" fullWidth type="submit">
                  Continue
                </LoadingButton>
                <Button variant="text" fullWidth type="button" onClick={forgotPassword}>
                  Forgot Password
                </Button>
              </Stack>
            )}
            {!isLoading && showNotice()}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  )
}

export default Login
